import { Suspense, type ReactNode } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { cn } from '@worldcoo/ui/lib/utils';
import { trpc } from '@/lib/trpc';
import { useTranslation } from '@/lib/i18n';
import { DiamondGrid, diamondGlows } from '../primitives';
import { formatBandFigure, formatMillions } from '../figure-format';

/**
 * Points of sale is a business constant served by the query, so this page and the round-up page agree
 * (TEC-3840). It prints in full; the beneficiaries sum abbreviates, since seven digits is a magnitude.
 */
function PointsOfSale() {
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	const { language } = useTranslation();
	return <>{formatBandFigure(language, stats.storesCount)}</>;
}

function Beneficiaries() {
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	const { language } = useTranslation();
	return <>{formatMillions(language, stats.directBeneficiaries)}</>;
}

/** An em dash, not a zero: a figure that has not arrived must not read as a figure of none. */
function FigurePending() {
	return <span className="opacity-40">—</span>;
}

/** `whitespace-nowrap`: an overflow gets reported, a wrap silently shoves the two cards shingled below. */
function StatCard(props: { className?: string; value: ReactNode; label: string }) {
	return (
		<div className={cn('text-ink flex flex-col justify-center gap-2 rounded-3xl px-8 py-12', props.className)}>
			<p className="text-5xl leading-[1.3] font-medium tracking-[-0.02em] whitespace-nowrap tabular-nums md:text-[3.5rem]">
				{props.value}
			</p>
			<p className="text-sm leading-[1.5]">{props.label}</p>
		</div>
	);
}

/** The cards' coordinates stay centre-relative as in the hand-off, or they walk out of the frame between 1280 and 1440. */
export function Microdonations() {
	const { t } = useLingui();
	return (
		<section className="bg-linen relative overflow-hidden">
			{/* Measured off the hand-off (~0.28 ink peak); bottom-anchored so it stays put when the band grows. */}
			<DiamondGrid
				color="var(--color-ink)"
				opacity={0.7}
				masks={[diamondGlows({ x: '106px', y: 'calc(100% - 20px)' })]}
			/>

			<div className="relative mx-auto flex max-w-[1440px] flex-col gap-12 px-6 py-16 md:py-20 xl:h-[738px] xl:flex-row xl:items-center xl:gap-0 xl:px-0 xl:py-0">
				<div className="flex w-full max-w-[653px] flex-col gap-[13px] xl:ml-[109px]">
					<h2 className="text-ink text-4xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
						{t`The power of microdonations`}
					</h2>
					{/* Figma's one text node with a blank line: the gap is one 150% line, 24px. */}
					<div className="text-ink flex max-w-[611px] flex-col gap-6 text-base leading-[1.5] tracking-[-0.02em]">
						<p>
							<Trans>
								What began as an idea in <strong className="font-semibold">2012</strong> has become a real force for
								change. Our commitment is to keep moving forward, continuously backing innovation, transparency and
								social commitment, our core values.
							</Trans>
						</p>
						<p>
							{/* One `<strong>` per country, so commas stay regular and translators can reorder. */}
							<Trans>
								We currently operate directly in <strong className="font-semibold">Spain</strong>,{' '}
								<strong className="font-semibold">Italy</strong> and <strong className="font-semibold">Andorra</strong>,
								expanding our impact day by day.
							</Trans>
						</p>
					</div>
				</div>

				<div className="flex flex-col items-center xl:absolute xl:inset-0 xl:block">
					<StatCard
						className="bg-sand w-full max-w-[342px] rotate-2 xl:absolute xl:top-[97px] xl:left-1/2 xl:ml-[159px] xl:w-[342px]"
						value={
							<Suspense fallback={<FigurePending />}>
								<PointsOfSale />
							</Suspense>
						}
						label={t`Active points of sale.`}
					/>
					<StatCard
						className="bg-apricot -mt-5 w-full max-w-[342px] -rotate-3 xl:absolute xl:top-[272px] xl:left-1/2 xl:mt-0 xl:ml-[236px] xl:w-[342px]"
						value={
							<Suspense fallback={<FigurePending />}>
								<Beneficiaries />
							</Suspense>
						}
						label={t`Beneficiaries of our projects.`}
					/>

					{/* Counter-rotated: the SVG has the hand-off's 16.84deg baked in, so riding the card would give 23.16deg.
					    The exposed corners show olive, the map's own sea. A plain <img>: imgproxy does nothing to an SVG. */}
					<div className="bg-olive relative isolate -mt-5 aspect-[300/210] w-full max-w-[300px] rotate-[6.32deg] overflow-hidden rounded-3xl xl:absolute xl:top-[451px] xl:left-1/2 xl:mt-0 xl:ml-[162px] xl:w-[300px]">
						<img
							src="/redesign/about/europe-map.svg"
							/* Decorative: the paragraph beside it already names the three countries the pins mark. */
							alt=""
							aria-hidden
							className="absolute inset-0 size-full -rotate-[6.32deg] object-cover"
						/>
					</div>
				</div>
			</div>
		</section>
	);
}
